/** Eine Zeile pro fälliger Aufgabe: Pflanze, Aufgabe, „Warum?“, Abhaken. */
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { fmtDueRelative, type TaskStatus } from '@pflanzen/shared';
import { usePhoto } from '../lib/hooks';
import { cx, Pill } from './kit';

/** Foto der Pflanze oder ihr Emoji. `size` = Pixel; ohne size füllt es die Breite (quadratisch). */
export function PlantAvatar({ photoId, emoji, size }: { photoId: string | null; emoji: string; size?: number }) {
  const url = usePhoto(photoId);
  const style = size ? { width: size, height: size } : undefined;
  const box = size ? 'shrink-0 rounded-2xl' : 'aspect-square w-full rounded-[20px]';
  return (
    <div className={`grid place-items-center overflow-hidden bg-moss-soft ${box}`} style={style}>
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" />
      ) : (
        <span style={{ fontSize: size ? size * 0.5 : 52 }}>{emoji}</span>
      )}
    </div>
  );
}

/** Status-Text der Aufgabe (fällig, überfällig, in X Tagen …). */
function stateLabel(s: TaskStatus): { text: string; tone: 'alert' | 'water' | 'soil' | 'sun' } {
  if (s.state === 'overdue') return { text: fmtDueRelative(s.daysUntilDue ?? 0), tone: 'alert' };
  if (s.state === 'due') return { text: 'heute', tone: 'water' };
  if (s.state === 'snoozed') return { text: 'später', tone: 'sun' };
  if (s.state === 'paused') return { text: s.pausedReason ?? 'pausiert', tone: 'soil' };
  return { text: fmtDueRelative(s.daysUntilDue ?? 0), tone: 'soil' };
}

/**
 * Eine Aufgaben-Zeile. `onPlant` = auf der Seite einer Pflanze: zeigt die Aufgabe
 * statt der Pflanze; nicht fällige Aufgaben bekommen einen dezenten Knopf.
 */
export function TaskRow(props: { status: TaskStatus; onCheck: (s: TaskStatus) => void; compact?: boolean; onPlant?: boolean }) {
  const { status: s, onCheck, compact = false, onPlant = false } = props;
  const [why, setWhy] = useState(false);
  const label = stateLabel(s);
  const done = s.handledToday;
  const due = s.state === 'due' || s.state === 'overdue';
  const title = onPlant ? `${s.type.icon} ${s.type.action}` : s.plant.name;
  // Auf der Pflanzenseite: Name des Typs nur, wenn er etwas anderes sagt als die Aktion
  const sub = onPlant ? (s.type.label !== s.type.action ? s.type.label : '') : `${s.type.icon} ${s.type.action}`;
  const btnText = done ? '✓' : s.type.fingerTest ? 'Prüfen' : 'Erledigt';
  const btnTone = done
    ? 'bg-moss-soft text-moss'
    : due
      ? 'bg-moss text-paper'
      : 'border border-line bg-paper text-moss';
  return (
    <div className={cx('rise rounded-3xl border border-line bg-card p-3 transition-opacity', done && 'opacity-55')}>
      <div className="flex items-center gap-3">
        {!onPlant && (
          <PlantAvatar photoId={s.plant.coverPhotoId} emoji={s.plant.emoji ?? s.species?.emoji ?? '🪴'} size={compact ? 44 : 52} />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold">{title}</div>
          <div className="flex flex-wrap items-center gap-1.5 text-sm text-muted">
            {sub && <span className="whitespace-nowrap">{sub}</span>}
            <Pill tone={label.tone}>{label.text}</Pill>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onCheck(s)}
          disabled={done}
          className={cx('grid h-12 w-[84px] shrink-0 place-items-center rounded-2xl text-sm font-semibold active:scale-95', btnTone)}
          aria-label={`${s.plant.name}: ${s.type.action}`}
        >
          {btnText}
        </button>
      </div>
      {!compact && s.why.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setWhy(!why)}
            className="-mb-3 -ml-2 flex min-h-11 items-center gap-1 px-3 text-xs font-semibold text-muted"
            aria-expanded={why}
          >
            Warum?
            <ChevronDown className={cx('h-3.5 w-3.5 transition-transform', why && 'rotate-180')} />
          </button>
          {why && (
            <ul className="mt-1.5 space-y-0.5 rounded-2xl bg-paper px-3 py-2 text-xs text-muted">
              {s.why.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
